import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Loader2, Check, RotateCcw, X, ChevronDown } from "lucide-react";
import { generateAiContent } from "../../utils/aiApi";
import { useTemplateSafe } from "./TemplateContext";

/* ────────────────────────────────────────────
   AI Content Writer Button
   
   A ✨ button that appears next to editable text
   elements. Opens a popover with:
   - Auto-detected context
   - Optional custom prompt
   - Generate / Accept / Regenerate / Cancel
   ──────────────────────────────────────────── */

/** Preset prompt suggestions */
const PROMPT_PRESETS: { label: string; value: string }[] = [
  { label: "\u05E9\u05D9\u05D5\u05D5\u05E7\u05D9 \u05D9\u05D5\u05EA\u05E8", value: "\u05DB\u05EA\u05D5\u05D1 \u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9 \u05D5\u05DE\u05E9\u05DB\u05E0\u05E2 \u05D9\u05D5\u05EA\u05E8" },
  { label: "\u05E7\u05E6\u05E8 \u05D5\u05EA\u05DE\u05E6\u05D9\u05EA\u05D9", value: "\u05E7\u05E6\u05E8 \u05D9\u05D5\u05EA\u05E8, \u05EA\u05DE\u05E6\u05D9\u05EA\u05D9 \u05D5\u05D9\u05E9\u05D9\u05E8" },
  { label: "\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9 \u05D5\u05D0\u05DE\u05D9\u05DF", value: "\u05D8\u05D5\u05DF \u05DE\u05E7\u05E6\u05D5\u05E2\u05D9, \u05D0\u05DE\u05D9\u05DF \u05D5\u05E1\u05DE\u05DB\u05D5\u05EA\u05D9" },
  { label: "\u05E8\u05E9\u05DE\u05D9 \u05D5\u05D7\u05DE\u05D5\u05D3", value: "\u05E1\u05D2\u05E0\u05D5\u05DF \u05E8\u05E9\u05DE\u05D9, \u05D7\u05DE\u05D5\u05D3 \u05D5\u05E0\u05D2\u05D9\u05E9" },
  { label: "\u05DE\u05E2\u05D5\u05E8\u05E8 \u05E4\u05E2\u05D5\u05DC\u05D4", value: "\u05D4\u05D5\u05E1\u05E3 \u05E7\u05E8\u05D9\u05D0\u05D4 \u05DC\u05E4\u05E2\u05D5\u05DC\u05D4 \u05D7\u05D6\u05E7\u05D4" },
];

/** Parse content key to extract page context */
function parseContentKey(key: string): { page: string; section: string } {
  const parts = key.split(":");
  return {
    page: parts[0] || "",
    section: parts.length > 1 ? parts.slice(1).join(" > ") : "",
  };
}

/** Human-readable labels for page parts */
const PAGE_LABELS: Record<string, string> = {
  hero: "\u05D0\u05D6\u05D5\u05E8 \u05E8\u05D0\u05E9\u05D9",
  home: "\u05E2\u05DE\u05D5\u05D3 \u05D1\u05D9\u05EA",
  about: "\u05D0\u05D5\u05D3\u05D5\u05EA",
  services: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
  "service-a": "\u05E9\u05D9\u05E8\u05D5\u05EA A",
  "service-b": "\u05E9\u05D9\u05E8\u05D5\u05EA B",
  products: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
  "product-a": "\u05DE\u05D5\u05E6\u05E8 A",
  "product-b": "\u05DE\u05D5\u05E6\u05E8 B",
  contact: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8",
  blog: "\u05D1\u05DC\u05D5\u05D2",
  consulting: "\u05D9\u05D9\u05E2\u05D5\u05E5",
  projects: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
  header: "\u05D4\u05D3\u05E8",
  footer: "\u05E4\u05D5\u05D8\u05E8",
  faq: "FAQ",
  benefits: "\u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA",
  testimonials: "\u05D4\u05DE\u05DC\u05E6\u05D5\u05EA",
  cta: "\u05E7\u05E8\u05D9\u05D0\u05D4 \u05DC\u05E4\u05E2\u05D5\u05DC\u05D4",
  store: "\u05D7\u05E0\u05D5\u05EA",
};

interface AiWriterButtonProps {
  /** The content key (e.g., "hero:h1") */
  contentKey: string;
  /** Current text content */
  currentContent: string;
  /** HTML element type (h1, p, span, etc.) */
  elementType: string;
  /** Callback when user accepts the generated content */
  onAccept: (newContent: string) => void;
  /** Optional: size variant */
  size?: "sm" | "md";
}

export function AiWriterButton({
  contentKey,
  currentContent,
  elementType,
  onAccept,
  size = "sm",
}: AiWriterButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userPrompt, setUserPrompt] = useState("");
  const [showPresets, setShowPresets] = useState(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const tplCtx = useTemplateSafe();

  // Parse context from content key
  const { page, section } = parseContentKey(contentKey);
  const pageLabel = PAGE_LABELS[page] || page;

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focus textarea when popover opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when closing
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setGeneratedContent(null);
    setError(null);
    setUserPrompt("");
    setShowPresets(false);
  }, []);

  // Generate content
  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedContent(null);

    try {
      const result = await generateAiContent({
        elementType,
        contentKey,
        currentContent,
        businessName: "",
        businessType: "",
        pageContext: pageLabel,
        userPrompt: userPrompt.trim() || undefined,
      });
      setGeneratedContent(result);
    } catch (err) {
      console.error("[AiWriter] Generation error:", err);
      setError(err instanceof Error ? err.message : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05EA\u05D5\u05DB\u05DF");
    } finally {
      setIsGenerating(false);
    }
  }, [elementType, contentKey, currentContent, pageLabel, userPrompt]);

  // Accept generated content
  const handleAccept = useCallback(() => {
    if (generatedContent) {
      onAccept(generatedContent);
      handleClose();
    }
  }, [generatedContent, onAccept, handleClose]);

  // Keyboard handler for the button
  const handleButtonKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
      }
    },
    [isOpen]
  );

  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const btnSize = size === "sm" ? "w-5 h-5" : "w-6 h-6";

  return (
    <span className="relative inline-block" style={{ zIndex: isOpen ? 100 : "auto" }}>
      {/* Trigger button */}
      <span
        ref={buttonRef}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        onKeyDown={handleButtonKeyDown}
        className={`${btnSize} bg-zap text-white rounded-full flex items-center justify-center hover:bg-zap-dark shadow-sm cursor-pointer select-none`}
        title={"\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF \u05E2\u05DD AI"}
      >
        <Sparkles className={iconSize} />
      </span>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 z-[98] sm:hidden bg-black/20"
            onClick={handleClose}
          />
          <div
            ref={popoverRef}
            dir="rtl"
            className="absolute z-[99] top-full mt-2 left-0 w-[320px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            style={{ fontFamily: "var(--font-family)" }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-l from-zap to-zap-light text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm" style={{ fontWeight: 600 }}>
                  {"\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF \u05E2\u05DD AI"}
                </span>
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={handleClose}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleClose();
                }}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* Context info */}
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                {pageLabel && (
                  <span className="px-2 py-0.5 bg-zap-pale text-zap rounded-full" style={{ fontWeight: 500 }}>
                    {pageLabel}
                  </span>
                )}
                {section && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    {section}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-mono">
                  {elementType.toUpperCase()}
                </span>
              </div>

              {/* Current content preview */}
              {currentContent && (
                <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-2.5 border border-gray-100 leading-relaxed max-h-16 overflow-y-auto">
                  <span className="text-gray-500" style={{ fontWeight: 500 }}>{"\u05E0\u05D5\u05DB\u05D7\u05D9: "}</span>
                  {currentContent.length > 120
                    ? currentContent.substring(0, 120) + "..."
                    : currentContent}
                </div>
              )}

              {/* User prompt */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-gray-500" style={{ fontWeight: 500 }}>
                    {"\u05D4\u05E0\u05D7\u05D9\u05D5\u05EA (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)"}
                  </label>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPresets(!showPresets);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowPresets(!showPresets);
                      }
                    }}
                    className="flex items-center gap-0.5 text-[10px] text-zap hover:text-zap-dark cursor-pointer"
                    style={{ fontWeight: 500 }}
                  >
                    {"\u05E1\u05D2\u05E0\u05D5\u05E0\u05D5\u05EA \u05DE\u05D5\u05DB\u05E0\u05D9\u05DD"}
                    <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showPresets ? "rotate-180" : ""}`} />
                  </span>
                </div>

                {/* Preset chips */}
                {showPresets && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {PROMPT_PRESETS.map((preset) => (
                      <span
                        key={preset.label}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserPrompt(preset.value);
                          setShowPresets(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setUserPrompt(preset.value);
                            setShowPresets(false);
                          }
                        }}
                        className="px-2 py-1 text-[10px] bg-zap-pale text-zap rounded-full hover:bg-zap/10 cursor-pointer"
                        style={{ fontWeight: 500 }}
                      >
                        {preset.label}
                      </span>
                    ))}
                  </div>
                )}

                <textarea
                  ref={textareaRef}
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder={"\u05DC\u05DE\u05E9\u05DC: \u201C\u05D9\u05D5\u05EA\u05E8 \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9\u201D, \u201C\u05E7\u05E6\u05E8 \u05D9\u05D5\u05EA\u05E8\u201D, \u201C\u05D4\u05D5\u05E1\u05E3 CTA\u201D..."}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none outline-none focus:border-zap/50 focus:ring-1 focus:ring-zap/20 placeholder:text-gray-300"
                  rows={2}
                  dir="rtl"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
              </div>

              {/* Generate button */}
              {!generatedContent && !isGenerating && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerate();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-zap text-white rounded-lg hover:bg-zap-dark text-sm cursor-pointer select-none shadow-sm"
                  style={{ fontWeight: 600 }}
                >
                  <Sparkles className="w-4 h-4" />
                  {"\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF"}
                </span>
              )}

              {/* Loading state */}
              {isGenerating && (
                <div className="flex items-center justify-center gap-2 py-4 text-zap">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm" style={{ fontWeight: 500 }}>
                    {"\u05D9\u05D5\u05E6\u05E8 \u05EA\u05D5\u05DB\u05DF..."}
                  </span>
                </div>
              )}

              {/* Error state */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 leading-relaxed">
                  {error}
                </div>
              )}

              {/* Generated content preview */}
              {generatedContent && (
                <div className="space-y-3">
                  <div className="bg-zap-pale/50 border border-zap/10 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3 h-3 text-zap" />
                      <span className="text-[10px] text-zap" style={{ fontWeight: 600 }}>
                        {"\u05EA\u05D5\u05E6\u05D0\u05D4"}
                      </span>
                    </div>
                    <p
                      className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto"
                      dir="rtl"
                    >
                      {generatedContent}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAccept();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleAccept();
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs cursor-pointer select-none"
                      style={{ fontWeight: 600 }}
                    >
                      <Check className="w-3.5 h-3.5" />
                      {"\u05D0\u05E9\u05E8"}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerate();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleGenerate();
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs cursor-pointer select-none"
                      style={{ fontWeight: 600 }}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClose();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleClose();
                        }
                      }}
                      className="py-2 px-3 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 text-xs cursor-pointer select-none"
                      style={{ fontWeight: 600 }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </span>
  );
}
